import React, { useMemo, useRef, useState } from "react";
import "./index.scss";
import { Document, pdfjs } from "react-pdf";
import { Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import PageCanvas from "./page-canvas";
import {
  ACCEPTED_SIGNATURE_TYPES,
  DEFAULT_PLACEMENT_WIDTH_FRACTION,
  PAGE_WIDTH,
  generateSignedPdf,
  getImageDimensions,
  getPdfFileBaseName,
} from "./utils";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

/**
 * Props:
 *   initialPdfUrl    – auto-load this PDF (by URL) instead of requiring a manual upload.
 *   initialPdfLabel  – display name shown for the auto-loaded PDF.
 *   onSave(blob)     – when provided, called with the signed PDF Blob instead of downloading it.
 *   saveButtonLabel  – label for the save action.
 */
const Home = ({ initialPdfUrl = null, initialPdfLabel = "", onSave = null, saveButtonLabel = "Save signed PDF" }) => {
  const [pdfFile, setPdfFile] = useState(initialPdfUrl);
  const [showPdfUpload, setShowPdfUpload] = useState(!initialPdfUrl);
  const [signatureFile, setSignatureFile] = useState(null);
  const [signatureUrl, setSignatureUrl] = useState("");
  const [signatureAspect, setSignatureAspect] = useState(0.4);
  const [numPages, setNumPages] = useState(0);
  const [placements, setPlacements] = useState({});
  const [pageHeights, setPageHeights] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const pdfInputRef = useRef(null);
  const signatureInputRef = useRef(null);

  const hasAnyPlacement = useMemo(
    () => Object.values(placements).some(Boolean),
    [placements]
  );

  const handlePdfUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (file.type !== "application/pdf") {
      toast.error("Please upload a PDF file");
      return;
    }
    setPdfFile(file);
    setNumPages(0);
    setPlacements({});
    setPageHeights({});
  };

  const handleSignatureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (!ACCEPTED_SIGNATURE_TYPES.includes(file.type)) {
      toast.error("Please upload a PNG or JPG signature image");
      return;
    }
    try {
      const { width, height } = await getImageDimensions(file);
      setSignatureAspect(height / width);
      setSignatureFile(file);
      setSignatureUrl(URL.createObjectURL(file));
      setPlacements({});
    } catch (error) {
      toast.error("Could not read the signature image");
    }
  };

  const placeSignature = (pageNumber) => {
    const width = DEFAULT_PLACEMENT_WIDTH_FRACTION;
    const containerHeight = pageHeights[pageNumber] || PAGE_WIDTH * 1.414;
    const height = (width * PAGE_WIDTH * signatureAspect) / containerHeight;
    setPlacements((prev) => ({
      ...prev,
      [pageNumber]: {
        xFrac: 0.1,
        yFrac: 0.8,
        wFrac: width,
        hFrac: height,
      },
    }));
  };

  const removeSignature = (pageNumber) => {
    setPlacements((prev) => ({ ...prev, [pageNumber]: null }));
  };

  const updatePlacement = (pageNumber, patch) => {
    setPlacements((prev) => ({
      ...prev,
      [pageNumber]: { ...prev[pageNumber], ...patch },
    }));
  };

  const applyToAllPages = () => {
    const [sourcePage] =
      Object.entries(placements).find(([, value]) => Boolean(value)) || [];
    if (!sourcePage) {
      toast.error("Place the signature on at least one page first");
      return;
    }
    const source = placements[sourcePage];
    const next = {};
    for (let page = 1; page <= numPages; page += 1) {
      next[page] = { ...source };
    }
    setPlacements(next);
  };

  const startOver = () => {
    setPdfFile(initialPdfUrl);
    setShowPdfUpload(!initialPdfUrl);
    setSignatureFile(null);
    setSignatureUrl("");
    setNumPages(0);
    setPlacements({});
    setPageHeights({});
    if (pdfInputRef.current) {
      pdfInputRef.current.value = "";
    }
    if (signatureInputRef.current) {
      signatureInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    if (!hasAnyPlacement) {
      toast.error("Place the signature on at least one page before saving");
      return;
    }
    setIsSaving(true);
    try {
      const signedBlob = await generateSignedPdf({ pdfFile, signatureFile, placements });
      if (onSave) {
        await onSave(signedBlob);
      } else {
        saveAs(signedBlob, `${getPdfFileBaseName(pdfFile)}-signed.pdf`);
        toast.success("Signed PDF downloaded");
      }
    } catch (error) {
      toast.error("Could not generate the signed PDF");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="pdf-signature-manager-home">
      <Paper elevation={2} className="pdf-signature-manager-uploads">
        <Box className="pdf-signature-manager-uploads-fields">
          <Box className="upload-field">
            {showPdfUpload ? (
              <>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                >
                  1. Upload PDF
                  <input
                    ref={pdfInputRef}
                    type="file"
                    accept="application/pdf"
                    hidden
                    onChange={handlePdfUpload}
                  />
                </Button>
                {pdfFile?.name && (
                  <Typography variant="body2" className="file-name">
                    {pdfFile.name}
                  </Typography>
                )}
                {initialPdfUrl && (
                  <Button size="small" onClick={startOver}>
                    Use the loaded document instead
                  </Button>
                )}
              </>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip icon={<DescriptionIcon />} label={initialPdfLabel || "Loaded document"} />
                <Button size="small" onClick={() => setShowPdfUpload(true)}>
                  Use a different file
                </Button>
              </Stack>
            )}
          </Box>
          <Box className="upload-field">
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
            >
              2. Upload signature
              <input
                ref={signatureInputRef}
                type="file"
                accept="image/png,image/jpeg"
                hidden
                onChange={handleSignatureUpload}
              />
            </Button>
            {signatureUrl && (
              <img
                className="signature-preview"
                src={signatureUrl}
                alt="signature preview"
              />
            )}
          </Box>
        </Box>
        {pdfFile && signatureFile && (
          <Box className="pdf-signature-manager-reset">
            <Button variant="outlined" color="primary" onClick={startOver}>
              Reset and upload again
            </Button>
          </Box>
        )}
      </Paper>

      {pdfFile && signatureFile && (
        <Paper elevation={2} className="pdf-signature-manager-workspace">
          <Box className="pdf-signature-manager-toolbar">
            <Stack direction="row" spacing={1.5}>
              <Button variant="outlined" onClick={applyToAllPages}>
                Apply to all pages
              </Button>
            </Stack>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : saveButtonLabel}
            </Button>
          </Box>

          <Document
            file={pdfFile}
            onLoadSuccess={({ numPages: total }) => setNumPages(total)}
            onLoadError={() => toast.error("Could not open this PDF")}
            className="pdf-signature-manager-document"
          >
            {Array.from({ length: numPages }, (_, index) => index + 1).map(
              (pageNumber) => (
                <PageCanvas
                  key={pageNumber}
                  pageNumber={pageNumber}
                  signatureUrl={signatureUrl}
                  placement={placements[pageNumber]}
                  pageHeight={pageHeights[pageNumber]}
                  onRenderSuccess={(page, size) =>
                    setPageHeights((prev) => ({ ...prev, [page]: size.height }))
                  }
                  onPlaceSignature={() => placeSignature(pageNumber)}
                  onRemoveSignature={() => removeSignature(pageNumber)}
                  onPlacementChange={updatePlacement}
                />
              )
            )}
          </Document>
        </Paper>
      )}
    </div>
  );
};

export default Home;
